import { NotificationsManager } from "@core/managers/notifications.manager";
import { UserStorageManager } from "@core/managers/user-storage.manager";
import { Logger } from "@core/helpers/logger.helpers";
import { EtabProfileService, MultiAccountService } from "@core/services";
import { EventManager } from "@core/managers/event.manager";
import api from "@core/plugins/api";
import { i18n } from "@core/plugins/i18n";
import { CORE_EVENTS, X_AUTH_SUB_ETAB, X_AUTH_USER_SUPERVISOR } from "@core/constants";
import { SupervisedEtabModel } from "@core/models";

let _supervisorInterceptorIdentifier;
let _supervisorModeStarted = false;
let _supervisedEtabId;

export class MultiAccountManager {
  static get hasStarted() {
    return _supervisorModeStarted;
  }

  static set hasStarted(value) {
    _supervisorModeStarted = value;
  }

  static get supervisedEtabId() {
    return _supervisedEtabId;
  }

  static set supervisedEtabId(value) {
    _supervisedEtabId = value;
  }

  static get interceptorIdentifier() {
    return _supervisorInterceptorIdentifier;
  }

  static set interceptorIdentifier(value) {
    _supervisorInterceptorIdentifier = value;
  }

  static async initSupervisorMode(selectedEtabId) {
    if (!MultiAccountManager.hasStarted) {
      const supervisorData = await MultiAccountService.getMultiAccountSupervisorData(selectedEtabId);
      const multiAccountSupervisor = new SupervisedEtabModel(supervisorData);
      if (multiAccountSupervisor.isValid()) {
        Logger.info("Supervisor init - masterEtab detected, start supervised etab mode");
        MultiAccountManager.startSupervisedEtabMode({ multiAccountSupervisor, shouldSaveData: true });
      }
    }
  }

  static async startSupervisedEtabMode({ multiAccountSupervisor, shouldSaveData } = {}) {
    if (!multiAccountSupervisor || !multiAccountSupervisor.isValid()) {
      return;
    }

    MultiAccountManager.hasStarted = true;
    MultiAccountManager.supervisedEtabId = multiAccountSupervisor.getSupervisedEtabId();

    if (shouldSaveData) {
      UserStorageManager.setSupervisorData(multiAccountSupervisor);
    }

    MultiAccountManager.useApiInterceptor({ multiAccountSupervisor });
    MultiAccountManager.registerOnLogoutEvent();
    MultiAccountManager.notifyUser();
    Logger.info("Supervised etab mode started");
  }

  static stopSupervisorMode() {
    MultiAccountManager.hasStarted = false;
    UserStorageManager.removeSupervisorData();
    MultiAccountManager.ejectApiInterceptor();
    MultiAccountManager.unregisterOnLogoutEvent();
    Logger.info("Supervised etab mode stopped");
  }

  static useApiInterceptor({ multiAccountSupervisor }) {
    if (!MultiAccountManager.interceptorIdentifier) {
      MultiAccountManager.interceptorIdentifier = api.private.interceptors.request.use(
        MultiAccountManager.interceptorBuilder({ multiAccountSupervisor }),
      );
      api.comet.interceptors.request.use(
        MultiAccountManager.interceptorBuilder({ multiAccountSupervisor }),
      );
    }
  }

  static ejectApiInterceptor() {
    if (MultiAccountManager.interceptorIdentifier) {
      api.private.interceptors.request.eject(MultiAccountManager.interceptorIdentifier);
    }
  }

  static interceptorBuilder({ multiAccountSupervisor }) {
    return (config) => {
      config.headers[X_AUTH_SUB_ETAB] = multiAccountSupervisor.getSupervisedEtabId();
      config.headers[X_AUTH_USER_SUPERVISOR] = multiAccountSupervisor.getMasterEtabId();

      return config;
    };
  }

  static registerOnLogoutEvent() {
    EventManager.on(CORE_EVENTS.ON_LOGOUT, MultiAccountManager.stopSupervisorMode);
  }

  static unregisterOnLogoutEvent() {
    EventManager.off(CORE_EVENTS.ON_LOGOUT, MultiAccountManager.stopSupervisorMode);
  }

  static async notifyUser() {
    const etabName = await EtabProfileService.getEtabName();
    NotificationsManager.create({
      message: i18n.global.t("INFO.@SUPERVISOR_MODE_ETAB", { etabName }),
      options: {
        timeout: 0,
        color: "black",
        icon: "warning",
        position: "bottom-left",
      },
    });
  }
}
